import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, Mail, Lock } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const Section = ({ title, children, icon: Icon }) => (
  <section className="bg-[#1a1a1a] border border-[#C4A55A]/20 rounded-2xl p-6 md:p-8 mb-6">
    <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
      {Icon && <Icon className="h-6 w-6 text-[#C4A55A]" />}
      {title}
    </h2>
    <div className="text-gray-300 space-y-3 leading-relaxed">{children}</div>
  </section>
);

export const Datenschutz = () => {
  return (
    <div className="bg-black min-h-screen text-white">
      <Header />
      <main className="container mx-auto px-4 pt-32 pb-16 max-w-4xl" data-testid="page-datenschutz">
        <Link
          to="/"
          className="inline-flex items-center text-[#C4A55A] hover:text-[#D4B56A] mb-6 text-sm"
          data-testid="datenschutz-back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zur Startseite
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-10 w-10 text-[#C4A55A]" />
          <h1 className="text-4xl md:text-5xl font-bold">Datenschutzerklärung</h1>
        </div>
        <p className="text-gray-400 mb-10">
          Diese Datenschutzerklärung erfüllt die Anforderungen des Schweizerischen Datenschutzgesetzes (revDSG)
          sowie der EU-Datenschutz-Grundverordnung (DSGVO).
        </p>

        <Section title="1. Verantwortlicher" icon={ShieldCheck}>
          <p>
            Verantwortlich für die Bearbeitung personenbezogener Daten auf dieser Website ist:
          </p>
          <div className="bg-black/30 rounded-lg p-4 border border-[#C4A55A]/15">
            <p className="font-semibold text-white">Löwenherz Invest AG</p>
            <p>Wildenstrasse 3, 8212 Neuhausen am Rheinfall, Schweiz</p>
            <p>
              E-Mail:{' '}
              <a href="mailto:office@loewenherz.ag" className="text-[#C4A55A] hover:text-[#D4B56A]">
                office@loewenherz.ag
              </a>
            </p>
            <p>Telefon: +41 52 55 101 40</p>
            <p>Vertreten durch: Matthias Kirchner (Verwaltungsrat)</p>
          </div>
        </Section>

        <Section title="2. Welche Daten wir bearbeiten">
          <p>Wir bearbeiten personenbezogene Daten, die uns durch folgende Anlässe übermittelt werden:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Kontaktformular:</strong> Name, E-Mail-Adresse, Telefon (optional), Nachricht</li>
            <li><strong>Investoren-Registrierung:</strong> Vor- und Nachname, E-Mail, Telefon, geplantes Investitionsvolumen</li>
            <li><strong>Vermittler-Registrierung:</strong> Vor- und Nachname, E-Mail-Adresse, Bankverbindung (sofern angegeben)</li>
            <li><strong>Server-Logfiles:</strong> IP-Adresse, Datum/Uhrzeit, abgerufene Seite, Browser-Informationen</li>
            <li><strong>Authentifizierung:</strong> Verschlüsselte Sitzungs-Cookies (HTTP-only) für angemeldete Nutzer</li>
          </ul>
        </Section>

        <Section title="3. Zweck und Rechtsgrundlage der Bearbeitung">
          <p>Die personenbezogenen Daten werden für folgende Zwecke bearbeitet:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Beantwortung von Anfragen über das Kontaktformular</li>
            <li>Verwaltung des Investoren- und Vermittlerverhältnisses</li>
            <li>Abwicklung von Aktienzeichnungen und Provisionsabrechnungen</li>
            <li>Erfüllung gesetzlicher und vertraglicher Pflichten</li>
            <li>Sicherstellung der technischen Funktionsfähigkeit und Sicherheit der Website</li>
          </ul>
          <p>
            Rechtsgrundlagen sind das berechtigte Interesse, die Vertragserfüllung,
            gesetzliche Verpflichtungen sowie ggf. die Einwilligung der betroffenen Person.
          </p>
        </Section>

        <Section title="4. Cookies und Tracking" icon={Lock}>
          <p>
            Diese Website verwendet ausschliesslich <strong>technisch notwendige Cookies</strong>.
            Sie dienen der Aufrechterhaltung der Login-Sitzung im Admin-, Vermittler- und Investorenbereich
            (HTTP-only, gesichert) und werden nach Beendigung der Sitzung gelöscht oder verlieren spätestens
            nach Ablauf der konfigurierten Gültigkeit ihre Wirkung.
          </p>
          <p>
            <strong>Es werden derzeit keine Tracking-Cookies, keine Google Analytics, keine Marketing-Pixel und keine Social-Media-Tracker eingesetzt.</strong>
          </p>
        </Section>

        <Section title="5. Hosting und Datenübermittlung">
          <p>
            Diese Website wird auf Servern der Contabo GmbH (Aschauer Strasse 32a, 81549 München, Deutschland)
            gehostet. Personenbezogene Daten werden somit in einem Land der Europäischen Union verarbeitet,
            das ein angemessenes Datenschutzniveau gewährleistet.
          </p>
          <p>
            E-Mail-Versand erfolgt über Hostpoint AG (Schweiz). Eine Übermittlung in Drittstaaten ohne
            angemessenes Schutzniveau findet nicht statt.
          </p>
        </Section>

        <Section title="6. Speicherdauer">
          <p>
            Personenbezogene Daten werden nur so lange aufbewahrt, wie es für die Erfüllung des jeweiligen
            Zwecks notwendig ist oder gesetzliche Aufbewahrungspflichten dies erfordern (z.B.
            handelsrechtliche Aufbewahrungspflicht von 10 Jahren gemäss OR Art. 958f).
          </p>
        </Section>

        <Section title="7. Datensicherheit">
          <p>
            Wir treffen angemessene technische und organisatorische Sicherheitsmassnahmen, um Ihre Daten
            gegen unbeabsichtigte oder unrechtmässige Vernichtung, Verlust, Veränderung oder gegen
            unbefugten Zugriff zu schützen. Hierzu zählen insbesondere TLS-Verschlüsselung (HTTPS),
            sichere Passwort-Hashing-Verfahren (bcrypt) und Brute-Force-Schutz.
          </p>
        </Section>

        <Section title="8. Ihre Rechte">
          <p>Sie haben gegenüber uns folgende Rechte hinsichtlich Ihrer personenbezogenen Daten:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Recht auf Auskunft</li>
            <li>Recht auf Berichtigung und Löschung</li>
            <li>Recht auf Einschränkung der Bearbeitung</li>
            <li>Recht auf Datenübertragbarkeit</li>
            <li>Widerspruchsrecht gegen die Bearbeitung</li>
            <li>Recht auf Widerruf einer erteilten Einwilligung</li>
            <li>Beschwerderecht beim Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten (EDÖB)
              bzw. bei einer EU-Datenschutzaufsichtsbehörde</li>
          </ul>
          <p>
            Zur Wahrnehmung Ihrer Rechte wenden Sie sich bitte per E-Mail an{' '}
            <a href="mailto:office@loewenherz.ag" className="text-[#C4A55A] hover:text-[#D4B56A]">
              office@loewenherz.ag
            </a>.
          </p>
        </Section>

        <Section title="9. Weitergabe an Dritte">
          <p>
            Eine Weitergabe Ihrer personenbezogenen Daten an Dritte erfolgt nur, sofern dies zur Erfüllung
            unserer vertraglichen Pflichten oder gesetzlicher Anforderungen erforderlich ist. Im Kontext
            der Aktienzeichnung kann eine Datenübermittlung an die das-aktienregister.ch Plattform
            (offizielles Schweizer Aktienregister) erfolgen, sofern Sie eine Zeichnung durchführen.
          </p>
        </Section>

        <Section title="10. Änderungen dieser Datenschutzerklärung">
          <p>
            Wir behalten uns vor, diese Datenschutzerklärung jederzeit anzupassen, damit sie stets den
            aktuellen rechtlichen Anforderungen und tatsächlichen Gegebenheiten entspricht. Es gilt die
            jeweils zum Zeitpunkt des Besuchs unserer Website aktuelle Fassung.
          </p>
        </Section>

        <div className="bg-[#C4A55A]/10 border border-[#C4A55A]/30 rounded-2xl p-6 md:p-8 mt-8">
          <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#C4A55A]" />
            Fragen zum Datenschutz?
          </h3>
          <p className="text-gray-300">
            Bei Fragen oder Anliegen zum Datenschutz kontaktieren Sie uns bitte per E-Mail unter{' '}
            <a href="mailto:office@loewenherz.ag" className="text-[#C4A55A] hover:text-[#D4B56A] font-medium">
              office@loewenherz.ag
            </a>{' '}
            oder telefonisch unter +41 52 55 101 40.
          </p>
        </div>

        <p className="text-gray-500 text-sm mt-10 text-center">
          Stand: {new Date().toLocaleDateString('de-CH', { day: '2-digit', month: 'long', year: 'numeric' })}
        </p>
      </main>
      <Footer />
    </div>
  );
};
